import { createContext, useContext, useRef, useState } from "react";
import { useParams } from "next/navigation";
import { CommentItem } from "pages/api/discuss/debate/[discussId]";
import { useInfiniteOpinionList } from "utils/hooks/DebateInfiniteList";
import {
  DebateSkeleton,
  OpinionDetailSkeleton,
} from "components/skeleton/discuss";
import { useRouter } from "next/router";
import { useRecoilState } from "recoil";
import { debateCleanModeState } from "states/debateState";
import { useDiscussIdContext } from "./DiscussIdContext";

interface DebateContextType {
  opinionList: CommentItem[];
  openComments: Set<number>;
  toggleComment: (opinionId: number) => void;
  isCommentDisabled: boolean;
  isClosed: boolean;
  userChoice: number | null;
  voteId: number | null;
  discussId: number;
  mutate: any;
  isLoadingMore?: boolean;
  isReachingEnd?: boolean;
  loadMore: any;
  sort: "new" | "like";
  setSort: any;
  registerMutate: (opinionId: number, mutateFn: () => void) => void;
  triggerMutate: (opinionId: number, ref?: any) => void;
  mutateMapRef: any;
  removeTopRef: (commentId: number) => void;
  tempRemoveTopRef: any;
  setAlarmTrigger?: any;
  setIsCleanMode: any;
  isCleanMode: boolean;
  isLoginUser: boolean;
  detailDiscussData: any;
}

const DebateContext = createContext<DebateContextType | null>(null);

export const DebateProvider = ({
  children,
  detailDiscussData,
  setAlarmTrigger = () => {},
  isLoginUser,
}: {
  children: React.ReactNode;
  setAlarmTrigger?: any;
  detailDiscussData: any;
  isLoginUser: boolean;
}) => {
  const { check } = require("korcen");
  const { discussId } = useDiscussIdContext();

  const [sort, setSort] = useState<"new" | "like">("new");
  // const [isCleanMode, setIsCleanMode] = useState(true);
  const [isCleanMode, setIsCleanMode] = useRecoilState(debateCleanModeState);

  const router = useRouter();
  const isOpinionDetailPage = router.pathname.startsWith(
    "/discuss/[slugId]/opinion"
  );

  // 투표 마감 상태, 유저 투표 상태, 투표 선택지 타입
  let isClosed = detailDiscussData?.isClosed;
  let userChoice = detailDiscussData?.userChoice;
  let voteId = detailDiscussData?.voteId || null;
  const {
    opinionList: opinionListData,
    isLoadingInit,
    isLoadingMore,
    isReachingEnd,
    loadMore,
    mutate,
  } = useInfiniteOpinionList({
    discussId: Number(discussId),
    sort,
  });
  const opinionList = opinionListData?.map((opinion: CommentItem) => {
    const isCensored = opinion?.description
      ? check(opinion?.description)
      : false;
    return {
      ...opinion,
      isCensored,
    };
  });

  const [openComments, setOpenComments] = useState<Set<number>>(new Set());

  // 의견+댓글 작성 불가 상태
  const isCommentDisabled = userChoice === null && isClosed ? true : false;

  const toggleComment = (opinionId: number) => {
    setOpenComments((prev) => {
      const newSet = new Set(prev);
      newSet.has(opinionId) ? newSet.delete(opinionId) : newSet.add(opinionId);
      return newSet;
    });
  };

  // 댓글 데이터 Muate
  const mutateMapRef = useRef<any>({});
  const tempRemoveTopRef = useRef<any>({});
  const registerMutate = (parent_id: number, mutate: () => void) => {
    mutateMapRef.current[parent_id] = mutate;
  };

  const triggerMutate = (parent_id: number) => {
    // parent_id
    const rRef = mutateMapRef?.current?.[parent_id];
    console.log("rRef", rRef);
    rRef?.();
  };
  const removeTopRef = (id: number) => {
    tempRemoveTopRef.current[id] = true;
  };

  if (isLoadingInit && !isOpinionDetailPage)
    return (
      <div>
        <div
          className="h-[30px] w-full"
          style={{
            background:
              "linear-gradient(180deg, var(--gray2) 0%, var(--white) 100%)",
          }}
        ></div>
        <DebateSkeleton />
      </div>
    );

  if (isLoadingInit && isOpinionDetailPage)
    return (
      <div>
        <OpinionDetailSkeleton />
      </div>
    );

  return (
    <DebateContext.Provider
      value={{
        opinionList,
        openComments,
        toggleComment,
        isCommentDisabled,
        isClosed,
        userChoice,
        voteId,
        discussId: Number(discussId),
        mutate,
        isLoadingMore,
        isReachingEnd,
        loadMore,
        sort,
        setSort,
        registerMutate,
        triggerMutate,
        mutateMapRef,
        removeTopRef,
        tempRemoveTopRef,
        setAlarmTrigger,
        setIsCleanMode,
        isCleanMode,
        isLoginUser,
        detailDiscussData,
      }}
    >
      {children}
    </DebateContext.Provider>
  );
};

// 커스텀 훅으로 사용
export const useDebateContext = () => {
  const context = useContext(DebateContext);
  if (!context)
    throw new Error("useDebateContext must be used within DebateProvider");
  return context;
};
